import { ExtendedNextLink } from 'components/Basic/ExtendedNextLink/ExtendedNextLink';
import { LoadingOverlay } from 'components/Basic/LoadingOverlay/LoadingOverlay';
import { useCurrentCartInfo } from 'connectors/cart/Cart';
import { useDomainConfig } from 'context/DomainConfigProvider/DomainConfigProvider';
import { getSellingPriceByDomain, useFormatPrice } from 'hooks/formatting/useFormatPrice';
import useTranslation from 'next-translate/useTranslation';
import { MenuBasketIcon } from 'public/svg/IconsSvg';
import { memo } from 'react';
import Skeleton from 'react-loading-skeleton';
import { usePersistStore } from 'store/usePersistStore';
import { getInternationalizedStaticUrls } from 'utils/getInternationalizedStaticUrls';
import twMerge from 'utils/twMerge';

type CartProps = {
    isDesktop: boolean;
};

const Cart: FC<CartProps> = ({ isDesktop }) => {
    const testIdentifier = 'layout-header-cart-';

    const { t } = useTranslation();
    const { formatPriceNumber: formatPrice } = useFormatPrice();
    const { itemsCount, totalItemsPrice, loyaltyClubDiscount, isInitiallyLoaded } = useCurrentCartInfo(false);

    const domainConfig = useDomainConfig();
    const [cartUrl] = getInternationalizedStaticUrls(['/cart'], domainConfig.url);
    const { authLoading } = usePersistStore((s) => s);

    if (isDesktop) {
        return (
            <div className="flauthLoadingify-end relative">
                {!isInitiallyLoaded && authLoading !== 'not-loading' && <LoadingOverlay className="h-8 w-8" />}
                <ExtendedNextLink
                    className="defaultTransition flex items-end rounded-md pb-5 pr-2 pt-2.5 text-black no-underline hover:text-black hover:no-underline"
                    href={cartUrl}
                >
                    <>
                        <span className="relative flex text-h6">
                            <MenuBasketIcon className="h-6 w-6 stroke-white vl:stroke-black" />
                            {isInitiallyLoaded ? (
                                <span
                                    data-testid={testIdentifier + 'itemcount'}
                                    className={twMerge(
                                        'absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red text-[13px] font-bold text-white',
                                        itemsCount > 99 && 'h-6 w-6',
                                    )}
                                >
                                    {itemsCount}
                                </span>
                            ) : (
                                <Skeleton className="absolute -right-2 -top-2 flex h-5 w-5 rounded-full" />
                            )}
                        </span>
                        <div className="ml-4 font-bold leading-4" data-testid={testIdentifier + 'totalprice'}>
                            <div className="text-tiny font-normal">{t('Your cart')}</div>
                            {totalItemsPrice || isInitiallyLoaded ? (
                                formatPrice(
                                    (totalItemsPrice ? getSellingPriceByDomain(totalItemsPrice, domainConfig) : 0) -
                                        (loyaltyClubDiscount ?? 0),
                                    {
                                        explicitZero: true,
                                    },
                                )
                            ) : (
                                <Skeleton className="h-4 w-14" />
                            )}
                        </div>
                    </>
                </ExtendedNextLink>
            </div>
        );
    }

    return (
        <div className="relative border-x vl:border-none">
            {!isInitiallyLoaded && authLoading !== 'not-loading' && <LoadingOverlay className="h-8 w-8" />}
            <div className="flex h-11 w-11 cursor-pointer items-center justify-center text-description outline-0">
                <ExtendedNextLink
                    className="relative flex h-full w-full items-center justify-center text-white no-underline transition-colors hover:text-white hover:no-underline vl:text-primary"
                    href={cartUrl}
                >
                    <>
                        <MenuBasketIcon className="h-6 w-6 stroke-white vl:stroke-black" />
                        {isInitiallyLoaded ? (
                            <span className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red text-[13px] font-bold text-white">
                                {itemsCount}
                            </span>
                        ) : (
                            <Skeleton
                                className="absolute right-1 top-1 flex h-5 w-5 rounded-full"
                                highlightColor="#cdcdcd"
                            />
                        )}
                    </>
                </ExtendedNextLink>
            </div>
        </div>
    );
};

/* @component */
export default memo(Cart);
